/**
 * Client-side mirror of relay_common.settings_spec.resolve, driven by the spec served by
 * GET /settings/global. Used for bulk editing (computing "mixed" across a selection) and
 * for previews; the server stays the source of truth (GET /mappings/{id}/effective).
 */
import type {
  SettingLevel,
  SettingOriginName,
  SettingSpecEntry,
  SettingsMap,
  SettingsSpec,
  SettingValue,
} from "@/api/types";

export interface Resolved {
  effective: SettingsMap;
  origin: Record<string, SettingOriginName>;
}

/** Most specific level that has the key wins: mapping → guild → global → default. */
export function resolve(
  spec: SettingsSpec,
  global: SettingsMap = {},
  guild: SettingsMap = {},
  mapping: SettingsMap = {},
): Resolved {
  const effective: SettingsMap = {};
  const origin: Record<string, SettingOriginName> = {};
  const layers: Array<[SettingOriginName, SettingsMap]> = [
    ["mapping", mapping],
    ["guild", guild],
    ["global", global],
  ];
  for (const [key, s] of Object.entries(spec)) {
    let found = false;
    for (const [name, layer] of layers) {
      if (name !== "global" && !s.levels.includes(name as SettingLevel)) continue;
      if (Object.prototype.hasOwnProperty.call(layer, key)) {
        effective[key] = layer[key];
        origin[key] = name;
        found = true;
        break;
      }
    }
    if (!found) {
      effective[key] = s.default;
      origin[key] = "default";
    }
  }
  return { effective, origin };
}

/** Display order and labels for spec groups. Unknown groups are appended alphabetically. */
export const GROUP_LABELS: Record<string, string> = {
  identity: "Sender identity",
  content: "Content",
  filters: "Filters",
  sync: "Sync and delivery",
  creation: "Channel creation",
  alerts: "Alerts",
  panel: "Panel and data",
};
const GROUP_ORDER = Object.keys(GROUP_LABELS);

export interface SpecGroup {
  group: string;
  label: string;
  keys: string[];
}

/** Group spec keys by `group`, keeping spec order inside a group. */
export function groupSpec(
  spec: SettingsSpec,
  filter: (key: string, s: SettingSpecEntry) => boolean = () => true,
): SpecGroup[] {
  const by = new Map<string, string[]>();
  for (const [key, s] of Object.entries(spec)) {
    if (!filter(key, s)) continue;
    const list = by.get(s.group) ?? [];
    list.push(key);
    by.set(s.group, list);
  }
  const rank = (g: string) => {
    const i = GROUP_ORDER.indexOf(g);
    return i === -1 ? GROUP_ORDER.length : i;
  };
  return [...by.entries()]
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
    .map(([group, keys]) => ({ group, label: GROUP_LABELS[group] ?? humanize(group), keys }));
}

/** Keys that may be set at a given level. */
export function keysForLevel(spec: SettingsSpec, level: SettingLevel): string[] {
  return Object.entries(spec)
    .filter(([, s]) => s.levels.includes(level))
    .map(([k]) => k);
}

/** "show_author_name" → "Show author name". */
export function humanize(key: string): string {
  const s = key.replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function sameValue(a: SettingValue | undefined, b: SettingValue | undefined): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((x, i) => x === b[i]);
  }
  return a === b;
}

export type Aggregate =
  | { kind: "uniform"; value: SettingValue; origins: SettingOriginName[] }
  | { kind: "mixed"; values: SettingValue[]; origins: SettingOriginName[] };

/** Aggregate one key across several resolved mappings (for the bulk editor). */
export function aggregate(key: string, rows: Resolved[]): Aggregate | null {
  if (rows.length === 0) return null;
  const values: SettingValue[] = [];
  const origins = new Set<SettingOriginName>();
  for (const r of rows) {
    const v = r.effective[key];
    origins.add(r.origin[key]);
    if (!values.some((x) => sameValue(x, v))) values.push(v);
  }
  const originList = [...origins];
  return values.length === 1
    ? { kind: "uniform", value: values[0], origins: originList }
    : { kind: "mixed", values, origins: originList };
}

/** Short text for any setting value (tables, badges). */
export function formatValue(v: SettingValue | undefined): string {
  if (v === undefined) return "—";
  if (typeof v === "boolean") return v ? "On" : "Off";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "(none)";
  if (v === "") return "(empty)";
  return String(v);
}

/** Parse a text field back into a typed value for the spec; throws Error with a message. */
export function parseInput(s: SettingSpecEntry, raw: string): SettingValue {
  switch (s.type) {
    case "int": {
      if (!/^-?\d+$/.test(raw.trim())) throw new Error("Must be a whole number");
      const n = Number(raw.trim());
      if (s.min !== null && n < s.min) throw new Error(`Must be ≥ ${s.min}`);
      if (s.max !== null && n > s.max) throw new Error(`Must be ≤ ${s.max}`);
      return n;
    }
    case "list":
      return raw
        .split(/\r?\n|,/)
        .map((x) => x.trim())
        .filter(Boolean);
    case "bool":
      return raw === "true";
    case "url":
      if (raw && !/^https?:\/\//i.test(raw)) throw new Error("Must start with http:// or https://");
      return raw;
    default:
      return raw;
  }
}

/** Inverse of parseInput for seeding text inputs. */
export function toInput(v: SettingValue | undefined): string {
  if (v === undefined) return "";
  if (Array.isArray(v)) return v.join("\n");
  return String(v);
}

/**
 * Global-level overrides from GET /settings/global. C2 serves `values` as *effective*
 * values plus `overrides`; fall back to `values` for servers that send only overrides.
 */
export function globalOverrides(gs: { values: SettingsMap; overrides?: SettingsMap } | undefined): SettingsMap {
  if (!gs) return {};
  return gs.overrides ?? gs.values;
}

/** True when a pending patch sets or resets `key` (drives SettingRow `saving`). */
export function touchesKey(key: string, set?: SettingsMap, reset?: string[]): boolean {
  return (!!set && Object.prototype.hasOwnProperty.call(set, key)) || !!reset?.includes(key);
}

/** "read_only" → "Read only". */
export function humanChoice(c: string): string {
  const s = c.replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** "on (global)" style description of the value a key falls back to. */
export function describeFallback(value: SettingValue | undefined, from: string): string {
  return `${formatValue(value)} (${from})`;
}
